// src/hooks/useLeadTracking.tsx
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { getStateAbbreviation } from "../../utils/helper";
declare global {
  interface Window {
    LeadiDscript?: boolean;
  }
}

export const useLeadTracking = () => {
  const [certificateUrl, setCertificateUrl] = useState<string>("");
  const [leadId, setLeadId] = useState<string>("");
  const [zipCode, setZipCode] = useState<number>(0);
  const [userIp, setUserIp] = useState<string>("");
  const [state, setCountry] = useState<string>("");
  const [isInitialized, setIsInitialized] = useState<boolean>(false);

  // Function to get the latest certificate and leadId values
  const getLatestTrustedFormAndLeadId = useCallback(() => {
    // Get the latest certificate - try multiple possible selectors
    let certInput: HTMLInputElement | null = document.querySelector(
      'input[name="xxTrustedFormCertUrl"]'
    ) as HTMLInputElement;

    // Try alternative selectors if not found
    if (!certInput || !certInput.value) {
      certInput = document.querySelector(
        'input[name="xxTrustedFormCertUrl1"]'
      ) as HTMLInputElement;
    }

    // Get the latest leadId - try multiple possible selectors
    let leadIdInput: HTMLInputElement | null = document.querySelector(
      "#leadid_token"
    ) as HTMLInputElement;

    // If not found, try alternative selectors
    if (!leadIdInput || !leadIdInput.value) {
      leadIdInput = document.querySelector(
        'input[name="universal_leadid"]'
      ) as HTMLInputElement;
    }

    // Get values, ensuring we have strings
    const currentCert = certInput?.value || certificateUrl || "";
    const currentLeadId = leadIdInput?.value || leadId || "";

    // Only update state if values have changed to prevent infinite loops
    if (currentCert !== certificateUrl && currentCert) {
      setCertificateUrl(currentCert);
    }

    if (currentLeadId !== leadId && currentLeadId) {
      setLeadId(currentLeadId);
    }

    return {
      currentCert,
      currentLeadId,
      currentUserIp: userIp,
      currentZipCode: zipCode,
      currentState: state,
    };
  }, [certificateUrl, leadId, userIp, zipCode, state]);

  // Fetch zip code based on IP
  // Fetch zip code based on IP
  useEffect(() => {
    const fetchZipCode = async () => {
      try {
        // Try the first API (whatismyip) - Get IP first
        const ipResponse = await axios.get(
          "https://api.whatismyip.com/ip.php?key=2f9c5779813801d90c01484d7bc3da0f"
        );

        // Check if IP was returned
        if (ipResponse.data) {
          const ip = ipResponse.data;
          setUserIp(ip);

          // Now use the IP to get location data
          const locationResponse = await axios.get(
            `https://api.whatismyip.com/ip-address-lookup.php?key=2f9c5779813801d90c01484d7bc3da0f&output=json&input=${ip}`
          );

          if (
            locationResponse.data &&
            locationResponse.data.ip_address_lookup[0].postalcode
          ) {
            const postalcode =
              locationResponse.data.ip_address_lookup[0].postalcode;
            const region = locationResponse.data.ip_address_lookup[0].region;
            if (postalcode) {
              setZipCode(postalcode);
            }
            if (region) {
              setCountry(getStateAbbreviation(region));
            }
          } else {
            // Fallback to ipapi.co if location lookup fails
            try {
              const fallbackResponse = await axios.get(
                `https://ipapi.co/${ip}/json/`
              );

              if (fallbackResponse.data && fallbackResponse.data.postal) {
                setZipCode(fallbackResponse.data.postal);
                setCountry(fallbackResponse.data.region_code);
              }
            } catch (fallbackError) {
              console.error(
                "Error fetching zip code from fallback API:",
                fallbackError
              );
            }
          }
        }

        // Mark as initialized after we've attempted to get the data
        setIsInitialized(true);
      } catch (error) {
        console.error("Error fetching zip code from primary API:", error);

        // If primary API fails completely, try fallback
        try {
          const fallbackResponse = await axios.get("https://ipapi.co/json/");

          if (fallbackResponse.data && fallbackResponse.data.postal) {
            setZipCode(fallbackResponse.data.postal);
            setUserIp(fallbackResponse.data.ip);
            setCountry(fallbackResponse.data.region_code);
          } else if (fallbackResponse.data && fallbackResponse.data.ip) {
            setUserIp(fallbackResponse.data.ip);
          }
        } catch (fallbackError) {
          console.error("Error fetching from fallback API:", fallbackError);
        }

        setIsInitialized(true); // Mark as initialized even on error
      }
    };

    fetchZipCode();
  }, []);

  // Check for TrustedForm and LeadID
  useEffect(() => {
    // Add the TrustedForm script if it doesn't exist
    const addTrustedFormScript = () => {
      // Check if TrustedForm script is already on the page
      const trustedFormScript = document.querySelector(
        'script[src*="trustedform"]'
      );

      if (!trustedFormScript) {
        // console.log("Adding TrustedForm script...");
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src = "https://certificates.trustedform.com/certificates.js";
        document.head.appendChild(script);
      }
    };

    // Add TrustedForm script
    addTrustedFormScript();

    // Set up a limited number of checks instead of an indefinite interval
    let checkCount = 0;
    const maxChecks = 20; // Limit to 20 checks

    const intervalId = setInterval(() => {
      checkCount++;
      getLatestTrustedFormAndLeadId();

      // Stop checking after max attempts
      if (checkCount >= maxChecks) {
        clearInterval(intervalId);
        // console.log("Completed TrustedForm and LeadID checks");
      }
    }, 1000); // Check every second instead of every 500ms

    return () => clearInterval(intervalId);
  }, [getLatestTrustedFormAndLeadId]);

  // Initialize LeadID
  useEffect(() => {
    const checkForLeadIdScript = () => {
      // First check if leadId is already available in the DOM
      let leadIdInput = document.querySelector(
        "#leadid_token"
      ) as HTMLInputElement;

      if (leadIdInput && leadIdInput.value) {
        setLeadId(leadIdInput.value);
        return true; // Found the leadId
      }

      // Check if the leadId script is on the page
      const leadIdScript = document.querySelector('script[src*="leadid"]');

      // If Jornaya LeadID script doesn't exist, add it
      if (!leadIdScript && !window.LeadiDscript) {
        // console.log("Adding LeadID script...");
        // Create a flag to prevent duplicate script loading
        window.LeadiDscript = true;

        const script = document.createElement("script");
        script.type = "text/javascript";
        script.async = true;
        script.src = "https://api.leadid.com/js/leadid-widget.js";
        document.head.appendChild(script);
      }

      return false; // Didn't find leadId yet
    };

    // Initial check
    const foundLeadId = checkForLeadIdScript();

    if (foundLeadId) {
      return; // No need to set up interval if we already found it
    }

    // Set up limited checking
    let checkCount = 0;
    const maxChecks = 10; // Limit checks to prevent infinite loop

    const intervalId = setInterval(() => {
      checkCount++;
      const found = checkForLeadIdScript();

      // Stop checking if we found the leadId or reached max checks
      if (found || checkCount >= maxChecks) {
        clearInterval(intervalId);
        // console.log("Completed LeadID checks");
      }
    }, 1000); // Check every second

    return () => clearInterval(intervalId);
  }, []);

  // The function to use right before form submission
  const getSubmissionValues = useCallback(() => {
    // Get the current values first
    const { currentCert, currentLeadId } = getLatestTrustedFormAndLeadId();

    // Try one last time to find the values directly in the DOM for maximum freshness
    let certFinal = currentCert;
    let leadIdFinal = currentLeadId;

    // Final check for TrustedForm certificate
    const certInput = document.querySelector(
      'input[name="xxTrustedFormCertUrl1"]'
    ) as HTMLInputElement;
    if (certInput && certInput.value) {
      certFinal = certInput.value;
    }

    // Final check for LeadID
    const leadIdInput = document.querySelector(
      "#leadid_token"
    ) as HTMLInputElement;
    if (leadIdInput && leadIdInput.value) {
      leadIdFinal = leadIdInput.value;
    }

    // console.log("Final submission values:", {
    //   trustedFormUrl: certFinal,
    //   jornayaLeadId: leadIdFinal,
    //   userIp: userIp,
    //   zipCode: zipCode,

    // });

    return {
      trustedFormUrl: certFinal,
      jornayaLeadId: leadIdFinal,
      userIp: userIp,
      zipCode: zipCode,
      state: state,
    };
  }, [getLatestTrustedFormAndLeadId, userIp, zipCode]);

  return {
    certificateUrl,
    leadId,
    zipCode,
    state,
    userIp,
    isInitialized,
    getLatestTrustedFormAndLeadId,
    getSubmissionValues,
  };
};
